import { type ElementType, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Spinner, type SpinnerProps } from "ui/spinner";
import { mergeSx } from "ui/merge-sx";

import { contentPadding } from "components/partials/layout/layout";

const VERTICAL_BLEED = "1.5rem";

interface LoadingScreenProps {
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Full-bleed dark overlay with a centered spinner. Absolute-positioned and expanded by the
 * surrounding content padding so it covers the page gutters (`contentPadding`) as well as the
 * top offset.
 */
export const LoadingScreen = ({
  component = "div",
  className,
  children,
  sx,
}: LoadingScreenProps) => {
  const { left, right } = contentPadding;

  return (
    <Box
      component={component}
      className={className}
      sx={mergeSx(
        {
          backgroundColor: "rgba(0, 0, 0, .75)",
          position: "absolute",
          left: `-${left}`,
          top: `-${VERTICAL_BLEED}`,
          width: `calc(100% + ${left} + ${right})`,
          height: `calc(100% + ${VERTICAL_BLEED})`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        sx,
      )}>
      <Spinner />
      {children}
    </Box>
  );
};

export const LoadingSpinner = (props: SpinnerProps) => <Spinner {...props} />;
